import { useEffect, useRef, useState } from 'react';

import { useGoogleExchange } from '../hooks/useGoogleExchange';
import { isGoogleSignInAvailable } from '../utils/google-sign-in';

const GOOGLE_GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const GOOGLE_GSI_SCRIPT_ID = 'google-gsi-client-script';

interface GoogleCredentialResponse {
  credential: string;
}

interface GoogleIdConfiguration {
  client_id: string;
  callback: (response: GoogleCredentialResponse) => void;
}

interface GoogleButtonConfiguration {
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  width?: number;
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: GoogleIdConfiguration) => void;
          renderButton: (parent: HTMLElement, options: GoogleButtonConfiguration) => void;
        };
      };
    };
  }
}

/**
 * Incarca scriptul GIS o singura data, idempotent — apeleaza `onLoad` fie imediat
 * (daca scriptul e deja pe pagina si initializat), fie la evenimentul `load`.
 */
function loadGoogleScript(onLoad: () => void) {
  const existing = document.getElementById(GOOGLE_GSI_SCRIPT_ID) as HTMLScriptElement | null;
  if (existing) {
    if (window.google?.accounts?.id) {
      onLoad();
    } else {
      existing.addEventListener('load', onLoad, { once: true });
    }
    return;
  }

  const script = document.createElement('script');
  script.id = GOOGLE_GSI_SCRIPT_ID;
  script.src = GOOGLE_GSI_SCRIPT_SRC;
  script.async = true;
  script.defer = true;
  script.addEventListener('load', onLoad, { once: true });
  document.head.appendChild(script);
}

/** Latimea maxima acceptata de GIS pentru renderButton(). */
const GOOGLE_BUTTON_MAX_WIDTH = 400;

export default function GoogleSignInButton() {
  const available = isGoogleSignInAvailable();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const { handleCredential, isPending, errorMessage, showReenrollHint, navigateToReenroll } =
    useGoogleExchange();
  const handleCredentialRef = useRef(handleCredential);
  // FE-M12: scrisul in corpul randarii contrazicea `useAccountWaiting.ts:33-36` -- router-ul are
  // `future: { v7_startTransition: true }`, deci randari pot fi aruncate, iar o randare aruncata
  // tot muta referinta. Efectul, fara deps, ruleaza doar dupa randari COMMIT-uite.
  useEffect(() => {
    handleCredentialRef.current = handleCredential;
  });

  useEffect(() => {
    if (!available) return;
    loadGoogleScript(() => setScriptLoaded(true));
  }, [available]);

  useEffect(() => {
    if (!available || !scriptLoaded || !containerRef.current || !window.google) return;

    window.google.accounts.id.initialize({
      client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID as string,
      callback: (response) => handleCredentialRef.current(response.credential),
    });
    // `size: 'large'` e maximul GIS ofera - 40px. Nu exista optiune de 44px (WCAG/HIG); brandul
    // Google e impus prin renderButton() si nu poate fi marit manual fara sa incalcam
    // conditiile lor de branding. Exceptie asumata, nu de reparat aici.
    // GIS accepta doar un numar fix de pixeli pentru `width`, nu procent/`100%` - masuram
    // latimea reala a randului (acelasi wrapper cat "Autentificare"), clampata la maximul GIS,
    // ca butonul sa nu depaseasca ecranul pe mobil (a fost bug real: 400 fix iesea din card la 360px).
    const availableWidth = wrapperRef.current?.clientWidth ?? GOOGLE_BUTTON_MAX_WIDTH;
    window.google.accounts.id.renderButton(containerRef.current, {
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      width: Math.min(availableWidth, GOOGLE_BUTTON_MAX_WIDTH),
    });
    // Ruleaza o singura data cand scriptul devine disponibil — handleCredential vine dintr-un
    // mutation hook si isi schimba identitatea la fiecare render; `renderButton` apelat repetat
    // ar injecta un buton nou de fiecare data, nu doar actualiza callback-ul.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [available, scriptLoaded]);

  if (!available) return null;

  return (
    <div ref={wrapperRef} className="mobile-stack gap-responsive">
      <div
        className={
          isPending
            ? 'touch-target flex items-center justify-center mx-auto pointer-events-none opacity-50 cursor-not-allowed'
            : 'touch-target flex items-center justify-center mx-auto'
        }
      >
        <div ref={containerRef} />
      </div>
      {isPending && <p className="text-fluid-sm text-center">Se autentifică...</p>}
      {errorMessage && (
        <p role="alert" aria-live="polite" className="text-fluid-sm text-error text-center">
          {errorMessage}
        </p>
      )}
      {/* Acelasi tipar ca LoginPage: 401 acopera si "cont in editia trecuta" si "cont
          inexistent" - aratam doar linkul, nu presupunem cauza. */}
      {showReenrollHint && (
        <p className="text-fluid-sm text-center">
          Ai avut cont în ediția trecută?{' '}
          <button
            type="button"
            className="font-semibold underline hover:text-primary"
            onClick={navigateToReenroll}
          >
            Reînscrie-te în ediția curentă
          </button>
        </p>
      )}
    </div>
  );
}
